/**
 * AutoGrab API Usage Tracking Service
 * Logs all AutoGrab API calls for cost monitoring and analytics
 */

import { supabase } from '../lib/supabase';

// Estimated costs for AutoGrab services (in AUD)
// These are approximate costs and should be updated based on actual AutoGrab pricing
export const AUTOGRAB_PRICING = {
  vin_lookup: 0.75,        // $0.75 per VIN lookup
  valuation: 2.50,         // $2.50 per valuation
  ppsr: 3.50,              // $3.50 per PPSR report
  market_overlay: 0.50,    // $0.50 per market overlay
  car_analysis: 1.50,      // $1.50 per car analysis
  other: 0.50,             // $0.50 for other services
};

export type AutoGrabServiceType =
  | 'vin_lookup'
  | 'valuation'
  | 'ppsr'
  | 'market_overlay'
  | 'car_analysis'
  | 'other';

export interface AutoGrabUsageData {
  organizationId?: string;
  requestedBy?: string;
  serviceType: AutoGrabServiceType;
  creditsUsed?: number;
  metadata?: Record<string, any>;
}

class AutoGrabUsageService {
  /**
   * Log AutoGrab API usage to database
   */
  async logUsage(data: AutoGrabUsageData): Promise<void> {
    try {
      // Calculate cost based on service type
      const cost = AUTOGRAB_PRICING[data.serviceType] || 0;

      const record = {
        organization_id: data.organizationId || null,
        requested_by: data.requestedBy || null,
        service_type: data.serviceType,
        cost: cost,
        credits_used: data.creditsUsed || 0,
        metadata: data.metadata || {},
      };

      const { error } = await supabase
        .from('autograb_api_usage')
        .insert(record);

      if (error) {
        console.error('Error logging AutoGrab usage:', error);
      } else {
        console.log(`✅ AutoGrab usage logged: ${data.serviceType} - $${cost.toFixed(2)} AUD`);
      }
    } catch (error) {
      console.error('Error in logUsage:', error);
    }
  }

  /**
   * Get usage stats for an organization
   */
  async getOrganizationUsage(
    organizationId: string,
    startDate?: Date,
    endDate?: Date
  ): Promise<any[]> {
    try {
      let query = supabase
        .from('autograb_api_usage')
        .select('*')
        .eq('organization_id', organizationId)
        .order('created_at', { ascending: false });

      if (startDate) {
        query = query.gte('created_at', startDate.toISOString());
      }
      if (endDate) {
        query = query.lte('created_at', endDate.toISOString());
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching AutoGrab usage:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Error in getOrganizationUsage:', error);
      return [];
    }
  }

  /**
   * Get total cost for an organization
   */
  async getOrganizationTotalCost(
    organizationId: string,
    startDate?: Date,
    endDate?: Date
  ): Promise<{ totalCost: number; totalCalls: number }> {
    try {
      const usage = await this.getOrganizationUsage(organizationId, startDate, endDate);

      const totalCost = usage.reduce((sum, record) => sum + parseFloat(record.cost || 0), 0);
      const totalCalls = usage.length;

      return { totalCost, totalCalls };
    } catch (error) {
      console.error('Error in getOrganizationTotalCost:', error);
      return { totalCost: 0, totalCalls: 0 };
    }
  }
}

export const autograbUsageService = new AutoGrabUsageService();
